import { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { GoalsProvider } from './contexts/GoalsContext';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import DailyGoals from './pages/DailyGoals';
import Workouts from './pages/Workouts';
import WorkoutDetail from './pages/WorkoutDetail';
import WorkoutExecution from './pages/WorkoutExecution';
import Nutrition from './pages/Nutrition';
import Profile from './pages/Profile';
import Challenges from './pages/Challenges';
import BottomNav from './components/BottomNav';
import { supabase } from '../lib/supabaseClient';

type Page =
  | 'signin'
  | 'signup'
  | 'onboarding'
  | 'dashboard'
  | 'goals'
  | 'challenges'
  | 'workouts'
  | 'workout-detail'
  | 'workout-execution'
  | 'nutrition'
  | 'profile';

function AppContent() {
  const { user, loading } = useAuth();
  const [currentPage, setCurrentPage] = useState<Page>('signin');
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const checkOnboardingStatus = useCallback(async () => {
    if (!user) return;

    try {
      // Verificar has_completed_questionnaire no perfil
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('has_completed_questionnaire')
        .eq('id', user.id)
        .maybeSingle();

      // Se o perfil não existe ou houve erro, verificar localStorage primeiro
      if (error || !profileData) {
        // PRIMEIRO verificar se já completou no localStorage
        const localCompleted = localStorage.getItem('shapex:onboarding');
        
        // Se tem no localStorage, assumir que completou e criar perfil com flag true
        if (localCompleted) {
          await supabase
            .from('profiles')
            .upsert({ 
              id: user.id,
              has_completed_questionnaire: true 
            }, {
              onConflict: 'id'
            });
          setCurrentPage('dashboard');
          setCheckingOnboarding(false);
          return;
        }
        
        // Se não tem no localStorage, criar perfil com flag false
        const { data: newProfileData } = await supabase
          .from('profiles')
          .upsert({ 
            id: user.id,
            has_completed_questionnaire: false 
          }, {
            onConflict: 'id'
          })
          .select('has_completed_questionnaire')
          .maybeSingle();
        
        // Se ainda não completou, exibir onboarding
        if (!newProfileData || newProfileData.has_completed_questionnaire !== true) {
          setCurrentPage('onboarding');
          setCheckingOnboarding(false);
          return;
        }
      }

      // Se já completou o questionário, ir direto para dashboard
      if (profileData?.has_completed_questionnaire === true) {
        setCurrentPage('dashboard');
        setCheckingOnboarding(false);
        return;
      }

      // Se não completou, exibir onboarding
      setCurrentPage('onboarding');
      setCheckingOnboarding(false);
    } catch (error) {
      console.error('Erro ao verificar status do questionário:', error);
      // Em caso de erro, verificar localStorage como fallback
      const localCompleted = localStorage.getItem('shapex:onboarding');
      if (localCompleted) {
        // Se tem no localStorage, assumir que completou (mas ainda verificar banco)
        try {
          const { data } = await supabase
            .from('profiles')
            .select('has_completed_questionnaire')
            .eq('id', user.id)
            .maybeSingle();
          
          if (data?.has_completed_questionnaire === true) {
            setCurrentPage('dashboard');
            setCheckingOnboarding(false);
            return;
          }
        } catch (e) {
          console.error('Erro ao verificar banco após erro inicial:', e);
        }
      }
      // Se não tem no localStorage ou erro, exibir onboarding
      setCurrentPage('onboarding');
      setCheckingOnboarding(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      checkOnboardingStatus();
    } else if (!loading) {
      setCheckingOnboarding(false);
    }
  }, [user, loading, checkOnboardingStatus]);

  const handleNavigate = (page: string) => {
    setCurrentPage(page as Page);
  };


  const handleStartWorkout = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setCurrentPage('workout-execution');
  };


  if (loading || checkingOnboarding) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    if (currentPage === 'signup') {
      return (
        <SignUp
          onSuccess={() => setCurrentPage('onboarding')}
          onSwitchToSignIn={() => setCurrentPage('signin')}
        />
      );
    }

    return (
      <SignIn
        onSuccess={() => checkOnboardingStatus()}
        onSwitchToSignUp={() => setCurrentPage('signup')}
      />
    );
  }

  if (currentPage === 'onboarding') {
    return <Onboarding onComplete={() => setCurrentPage('dashboard')} />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigate} />;
      case 'goals':
        return <DailyGoals onGoWorkouts={() => setCurrentPage('workouts')} />;
      case 'challenges':
        return <Challenges />;
      case 'workouts':
        return <Workouts />;
      case 'workout-detail':
        return (
          <WorkoutDetail
            templateId={selectedTemplateId}
            onBack={() => setCurrentPage('workouts')}
            onStartWorkout={handleStartWorkout}
          />
        );
      case 'workout-execution':
        return (
          <WorkoutExecution
            templateId={selectedTemplateId}
            onBack={() => setCurrentPage('workout-detail')}
            onComplete={() => setCurrentPage('dashboard')}
          />
        );
      case 'nutrition':
        return <Nutrition />;
      case 'profile':
        return <Profile onRedoOnboarding={() => setCurrentPage('onboarding')} />;
      default:
        return <Dashboard onNavigate={handleNavigate} />;
    }
  };

  return (
    <>
      {renderPage()}
      <BottomNav currentPage={currentPage} onNavigate={handleNavigate} />
    </>
  );
}

import { GamificationProvider } from './contexts/GamificationContext';

function App() {
  return (
    <AuthProvider>
      <ProfileProvider>
        <GoalsProvider>
          <GamificationProvider>
            <AppContent />
          </GamificationProvider>
        </GoalsProvider>
      </ProfileProvider>
    </AuthProvider>
  );
}

export default App;
